/**
 * Messages for org policy refusals: the connect callback's ?cloud_error=… and
 * use-time 403 {detail, code} from picker-token / items / attach.
 */

import { useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import type { CloudConnectProvider, CloudFilesStatus } from '@/services/cloudFilesApi';
import type { CloudConnectErrorCode } from './useCloudConnect';

export const CONNECT_PROVIDER_LABEL: Record<CloudConnectProvider, string> = {
  google_drive: 'Google Drive',
  microsoft_files: 'OneDrive & SharePoint',
};

export function useCloudPolicyMessages() {
  const { t } = useAppTranslation();
  const { selectedOrganization } = useOrganization();
  const orgName = selectedOrganization?.name || t('messaging.cloud.thisOrg', 'this organization');

  const connectError = useCallback(
    (
      code: CloudConnectErrorCode,
      provider: CloudConnectProvider | null,
      status?: CloudFilesStatus | null,
    ): { title: string; description?: string } => {
      const providerLabel = provider ? CONNECT_PROVIDER_LABEL[provider] : t('messaging.cloud.cloudFiles', 'Cloud files');
      const domains = provider
        ? status?.[provider]?.allowed_domains ?? []
        : Array.from(new Set([...(status?.google_drive?.allowed_domains ?? []), ...(status?.microsoft_files?.allowed_domains ?? [])]));
      switch (code) {
        case 'org_disabled':
          return {
            title: t('messaging.cloud.errOrgDisabled', '{{provider}} is turned off for {{org}}', { provider: providerLabel, org: orgName }),
            description: t('messaging.cloud.errOrgDisabledHint', 'Ask an organization admin to enable it in Settings › Integrations.'),
          };
        case 'domain_not_allowed':
          return {
            title: t('messaging.cloud.errDomain', "That account isn't allowed for {{org}}", { org: orgName }),
            description: domains.length
              ? t('messaging.cloud.errDomainHint', 'Use an account on: {{domains}}', { domains: domains.join(', ') })
              : t('messaging.cloud.errUseAllowedAccount', 'Use an account your organization allows.'),
          };
        case 'tenant_not_allowed':
          return {
            title: t('messaging.cloud.errTenant', "That Microsoft 365 organization isn't allowed for {{org}}", { org: orgName }),
            description: t('messaging.cloud.errTenantHint', "Sign in with a work account from your organization's approved Microsoft 365 tenant."),
          };
        default:
          return { title: t('messaging.cloud.errConnect', 'Could not connect {{provider}}', { provider: providerLabel }) };
      }
    },
    [t, orgName],
  );

  return { connectError, orgName };
}
